import { extendObservable, runInAction } from 'mobx';

import { CompareController } from '../networking/controllers/CompareController';
import { ProductsController } from '../networking/controllers/ProductsController';
import { Logger } from '../helpers/Logger';

const MAX_AMOUNT_ALLOWED = 2;

class CompareStoreClass {
  constructor() {
    extendObservable(this, {
      sectionCollapsed: false,
      products: [],
      invalidComparison: false,
      validatingComparison: false,
      validationError: false,
      comparison: null,
      compareError: false,
    });
  }

  toggleCollapse() {
    runInAction(() => {
      this.sectionCollapsed = !this.sectionCollapsed;
    });
  }

  async toggleProduct(product) {
    const alreadyBeingCompared = this.products.find(prod => prod.id === product.id);
    if (alreadyBeingCompared) {
      this.removeProduct(product.id);
      return;
    }

    if (this.products.length === MAX_AMOUNT_ALLOWED) {
      return;
    }

    runInAction(() => {
      this.products.push(product);
    });

    if (this.products.length === MAX_AMOUNT_ALLOWED) {
      runInAction(() => {
        this.validatingComparison = true;
      });
      try {
        const validComparison = await CompareController.validateComparison(this.products);
        runInAction(() => {
          this.invalidComparison = !validComparison;
        });
      } catch (error) {
        Logger.warn(error);
        runInAction(() => {
          this.validationError = true;
        });
      } finally {
        runInAction(() => {
          this.validatingComparison = false;
        });
      }
    }
  }

  removeProduct(productId) {
    const updatedProducts = this.products.filter(product => product.id !== productId);
    runInAction(() => {
      this.products = updatedProducts;
      this.invalidComparison = false;
      this.validationError = false;
    });
  }

  removeAllProducts() {
    runInAction(() => {
      this.products = [];
      this.invalidComparison = false;
      this.validationError = false;
    });
  }

  async compare(productIds) {
    runInAction(() => {
      this.compareError = false;
    });
    try {
      if (!this.products.length) {
        const unsortedProducts = [];
        // Promise.all doesn't ensure the products arrive ordered correctly,
        // so we have to sort them after the fact.
        await Promise.all(productIds.map(async (productId, index) => {
          const product = await ProductsController.find(productId);
          unsortedProducts.push({
            product,
            index,
          });
        }));
        const sortedProducts = unsortedProducts.sort((a, b) => (
          a.index - b.index > 0 ? 1 : -1
        )).map(data => data.product);
        runInAction(() => {
          this.products = sortedProducts;
        });
      }
      const comparison = await CompareController.compare(productIds);
      runInAction(() => {
        this.comparison = comparison;
      });
    } catch (error) {
      Logger.warn(error);
      runInAction(() => {
        this.compareError = true;
      });
    }
  }

  clearComparison() {
    runInAction(() => {
      this.comparison = null;
      this.products = [];
      this.compareError = false;
    });
  }
}

const CompareStore = new CompareStoreClass();

export { CompareStore, MAX_AMOUNT_ALLOWED };



// WEBPACK FOOTER //
// ./src/stores/CompareStore.js